"use client";

import { c as _c } from "react/compiler-runtime";
import { convexClient } from "@convex-dev/better-auth/client/plugins";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { Atom, RegistryProvider } from "@effect-atom/atom-react";
import { ConvexClient, type ConvexClientService } from "@packages/confect/client";
import { anonymousClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { ConvexClient as ConvexBrowserClient } from "convex/browser";
import type { FunctionReference, FunctionReturnType } from "convex/server";
import { Effect, Layer, Stream } from "effect";
import { createContext, type ReactNode, useContext, useMemo } from "react";
function readConvexUrl() {
  const value = process.env.NEXT_PUBLIC_CONVEX_URL?.trim();
  return value && value.length > 0 ? value : null;
}
function getRequiredBrowserClient(client: ConvexBrowserClient | null) {
  if (client === null) {
    throw new Error("NEXT_PUBLIC_CONVEX_URL is required for Convex access");
  }
  return client;
}
function createConvexClientService(browserClient: ConvexBrowserClient | null): ConvexClientService {
  return {
    query: <Query extends FunctionReference<"query">,>(query: Query, args: Query["_args"]): Effect.Effect<FunctionReturnType<Query>> => Effect.promise(() => getRequiredBrowserClient(browserClient).query(query, args)),
    mutation: <Mutation extends FunctionReference<"mutation">,>(mutation: Mutation, args: Mutation["_args"]): Effect.Effect<FunctionReturnType<Mutation>> => Effect.promise(() => getRequiredBrowserClient(browserClient).mutation(mutation, args)),
    action: <Action extends FunctionReference<"action">,>(action: Action, args: Action["_args"]): Effect.Effect<FunctionReturnType<Action>> => Effect.promise(() => getRequiredBrowserClient(browserClient).action(action, args)),
    subscribe: <Query extends FunctionReference<"query">,>(query: Query, args: Query["_args"]): Stream.Stream<FunctionReturnType<Query>> => Stream.unwrap(Effect.sync(() => {
      const activeClient = getRequiredBrowserClient(browserClient);
      return Stream.async(emit => {
        const unsubscribe = activeClient.onUpdate(query, args, result => {
          emit.single(result);
        });
        return Effect.sync(() => unsubscribe());
      });
    }))
  };
}
const convexBrowserClient = (() => {
  const convexUrl = readConvexUrl();
  return convexUrl === null ? null : new ConvexBrowserClient(convexUrl);
})();
const authClient = createAuthClient({
  plugins: convexBrowserClient === null ? [anonymousClient()] : [anonymousClient(), convexClient()]
});
type AuthClient = typeof authClient;
const AuthClientContext = createContext<AuthClient | null>(null);
const AppConvexClientLayer = Layer.succeed(ConvexClient, createConvexClientService(convexBrowserClient));
export const atomRuntime = Atom.runtime(AppConvexClientLayer);
export function ConvexAuthProvider(t0) {
  const $ = _c(5);
  const {
    children,
    enableAuth: t1
  } = t0;
  const enableAuth = t1 === undefined ? true : t1;
  if (!enableAuth || convexBrowserClient === null) {
    let t2;
    if ($[0] !== children) {
      t2 = <AuthClientContext.Provider value={authClient}>{children}</AuthClientContext.Provider>;
      $[0] = children;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      setAuth: convexBrowserClient.setAuth.bind(convexBrowserClient),
      clearAuth: _temp
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const convexClientWithClearAuth = t2;
  let t3;
  if ($[3] !== children) {
    t3 = <AuthClientContext.Provider value={authClient}><ConvexBetterAuthProvider client={convexClientWithClearAuth} authClient={authClient}>{children}</ConvexBetterAuthProvider></AuthClientContext.Provider>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp() {
  return convexBrowserClient.client.clearAuth();
}
export function useAuthClient() {
  const authClient = useContext(AuthClientContext);
  if (!authClient) {
    throw new Error("useAuthClient must be used within an AuthClientProvider");
  }
  return authClient;
}
export const useSession = (props: {
  allowAnonymous?: boolean;
} = {
  allowAnonymous: true
}) => {
  const authClient = useAuthClient();
  const session = authClient.useSession();
  const isAnonymousUser = session?.data?.user?.isAnonymous ?? false;
  const shouldHideData = !props.allowAnonymous && isAnonymousUser;
  return useMemo(() => {
    if (shouldHideData) {
      return {
        ...session,
        data: null
      };
    }
    return session;
  }, [session, shouldHideData]);
};
export const useNonAnonymousSession = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      allowAnonymous: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useSession(t0);
};
export function ConvexClientProvider(t0) {
  const $ = _c(3);
  const {
    children,
    enableAuth: t1
  } = t0;
  const enableAuth = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== children || $[1] !== enableAuth) {
    t2 = <RegistryProvider><ConvexAuthProvider enableAuth={enableAuth}>{children}</ConvexAuthProvider></RegistryProvider>;
    $[0] = children;
    $[1] = enableAuth;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}